import React, { useState, useEffect } from 'react';
import { Card, Tag, Typography, Button, Space, Form, Select, Input, InputNumber, Switch, Slider, Divider, Tabs, DatePicker, Drawer, Upload, Modal, message, Row, Col, Radio, Checkbox, Table, Tooltip, Spin, Alert } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PlayCircleOutlined, StopOutlined, BugOutlined, UploadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { MOCK_TRIGGER_EVENT_NAMES } from './TriggerEventManage';

const { Title, Text } = Typography;
const { TextArea } = Input;

const REGIONS = [
    { key: 'domestic', label: '国内' },
    { key: 'asiaPacific', label: '亚太' },
    { key: 'northAmerica', label: '北美' },
    { key: 'europe', label: '欧洲' },
];

// TODO: 联调时替换为接口数据
const MOCK_AGENT_CONFIG = {
    key: '1', agentId: 'test-agent-001', name: 'test-家庭安防智能看护', description: '7×24小时家庭安全守护，自动识别异常事件并生成AI报告',
    status: 'published',
    captureMode: 'mixed',
    eventTypes: ['移动检测', '人形识别', '声音检测', '门铃呼叫', '烟雾检测'],
    timerInterval: 10,
    eventInterval: 5,
    dailyCaptureLimit: 200,
    recognitionEnabled: true,
    temperature: 0.2,
    systemPrompt: '你是一个家庭安防助手。分析摄像头画面，识别以下事件类型：\n- 陌生人入侵\n- 包裹送达\n- 宠物异常行为\n- 老人跌倒\n- 火灾/烟雾\n\n设备名称：${device_name}\n场景类型：${scene_type}\n事件类型：${event_type}', // eslint-disable-line no-template-curly-in-string
    outputFormatPrompt: '{"event":"事件类型","severity":"low|medium|high","summary":"一句话描述","detail":"详细分析"}',
    searchEnabled: true,
    searchRange: 7,
    searchTopK: 10,
    searchThreshold: 0.7,
    summaryEnabled: true,
    summaryPeriod: 'weekly',
    summaryRangeDays: 7,
    summaryLimit: 3,
    summaryPrompt: '基于以下${period}的事件数据，生成家庭安防总结报告：\n${events_list}\n\n上次总结：${previous_summary}', // eslint-disable-line no-template-curly-in-string
    summaryOutputFormat: '{"period":"时间范围","safety":"安全评估","anomaly":"异常事件","advice":"建议"}',
    notifyMethods: ['App推送'],
    // 按区域配置模型
    regionModels: {
        domestic: { model: 'doubao-vision-1.8' },
    },
    // 已勾选的区域
    checkedRegions: ['domestic'],
};

const STATUS_MAP = { draft: { text: '草稿', color: 'default' }, published: { text: '已发布', color: 'green' }, offline: { text: '已下线', color: 'red' } };
const MODEL_OPTIONS = ['doubao-vision-1.8', 'qwen-VL-Max', 'qwen-VL-Plus', 'doubao-1.8', 'qwen-Max', 'qwen-Plus'];
const EVENT_OPTIONS = MOCK_TRIGGER_EVENT_NAMES;
const NOTIFY_METHODS = ['App推送', '短信'];

const SECTIONS = [
    { key: 'basic', title: '① 基础信息' },
    { key: 'capture', title: '② 抓图策略' },
    { key: 'recognition', title: '③ 事件识别' },
    { key: 'search', title: '④ AI搜索' },
    { key: 'summary', title: '⑤ 阶段总结' },
    { key: 'modelSelect', title: '⑥ 选择模型' },
    { key: 'notify', title: '⑦ 通知配置' },
];

const AgentConfig = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isNew = id === 'new';
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [activeSection, setActiveSection] = useState('basic');
    const [pageTab, setPageTab] = useState('config');
    const [debugFilters, setDebugFilters] = useState({ dateRange: null, status: undefined });

    useEffect(() => {
        setLoading(true);
        setTimeout(() => {
            setConfig(isNew ? { ...MOCK_AGENT_CONFIG, agentId: '', name: '', status: 'draft', linkedPackages: 0 } : MOCK_AGENT_CONFIG);
            setLoading(false);
        }, 300);
    }, [id, isNew]);

    // ---------- 保存校验：选中的区域必须选模型 ----------
    const validateModels = () => {
        if (!config.checkedRegions || config.checkedRegions.length === 0) {
            message.warning('至少勾选一个服务区域并选择模型');
            return false;
        }
        for (const rk of config.checkedRegions) {
            const m = config.regionModels?.[rk];
            if (!m?.model) {
                const label = REGIONS.find(r => r.key === rk)?.label || rk;
                message.warning(`服务区域「${label}」未选择模型`);
                return false;
            }
        }
        return true;
    };

    const handleSave = () => {
        if (!validateModels()) return;
        message.success('已保存');
    };
    const handlePublish = () => {
        if (!validateModels()) return;
        Modal.confirm({
            title: '发布Agent', content: '发布后Agent可被套餐服务引用，确认发布？', okText: '确认发布',
            onOk: () => { setConfig(prev => ({ ...prev, status: 'published' })); message.success('Agent已发布'); navigate('/agent/list'); },
        });
    };
    const handleOffline = () => {
        Modal.confirm({
            title: '下线Agent', content: '下线后新设备无法激活该Agent，已购套餐不受影响。确认下线？', okText: '确认下线',
            onOk: () => { setConfig(prev => ({ ...prev, status: 'offline' })); message.success('Agent已下线'); },
        });
    };

    // 修改区域模型
    const setRegionModel = (regionKey, field, value) => {
        setConfig(prev => ({
            ...prev,
            regionModels: {
                ...prev.regionModels,
                [regionKey]: { ...(prev.regionModels?.[regionKey] || {}), [field]: value },
            },
        }));
    };

    const toggleRegion = (regionKey, checked) => {
        setConfig(prev => {
            const checkedRegions = checked
                ? [...(prev.checkedRegions || []), regionKey]
                : (prev.checkedRegions || []).filter(r => r !== regionKey);
            return { ...prev, checkedRegions };
        });
    };

    // TODO: 联调时替换为接口数据
    const MOCK_DEBUG_LOGS = [
        { key: '1', time: '2026-07-22 15:32:01', model: 'doubao-vision-1.8（国内）', image: 'camera_01_snap_001.jpg', inputContent: '分析摄像头画面，识别异常事件...', outputContent: '{"event":"移动侦测","severity":"low","summary":"检测到轻微移动"}', inputTokens: 56, outputTokens: 78, status: 'success', latency: 720, traceId: 'trace-a1b2c3' },
        { key: '2', time: '2026-07-22 15:31:45', model: 'doubao-vision-1.8（国内）', image: 'camera_01_snap_002.jpg', inputContent: '分析摄像头画面，识别异常事件...', outputContent: '{"event":"人形检测","severity":"medium","summary":"检测到人员出入"}', inputTokens: 89, outputTokens: 112, status: 'success', latency: 910, traceId: 'trace-d4e5f6' },
        { key: '3', time: '2026-07-22 15:30:12', model: 'doubao-vision-1.8（国内）', image: 'camera_01_snap_003.jpg', inputContent: '分析摄像头画面，识别异常事件...', outputContent: '', inputTokens: 0, outputTokens: 0, status: 'failed', latency: 3000, traceId: 'trace-g7h8i9' },
        { key: '4', time: '2026-07-22 15:29:50', model: 'doubao-vision-1.8（国内）', image: 'camera_01_snap_004.jpg', inputContent: '分析摄像头画面，识别异常事件...', outputContent: '{"event":"宠物检测","severity":"low","summary":"检测到宠物活动"}', inputTokens: 42, outputTokens: 56, status: 'success', latency: 650, traceId: 'trace-j1k2l3' },
        { key: '5', time: '2026-07-22 15:28:33', model: 'doubao-vision-1.8（国内）', image: 'camera_01_snap_005.jpg', inputContent: '分析摄像头画面，识别异常事件...', outputContent: '{"event":"门铃呼叫","severity":"high","summary":"检测到门铃按下"}', inputTokens: 102, outputTokens: 165, status: 'success', latency: 1100, traceId: 'trace-m4n5o6' },
    ];

    const [filteredLogs, setFilteredLogs] = useState(MOCK_DEBUG_LOGS);
    const [debugDrawer, setDebugDrawer] = useState({ open: false });
    const [debugLoading, setDebugLoading] = useState(false);
    const [debugResult, setDebugResult] = useState(null);
    const [debugFile, setDebugFile] = useState(null);

    const filteredDebugLogs = debugFilters.status
        ? filteredLogs.filter(log => log.status === debugFilters.status)
        : filteredLogs;
    const debugStats = { totalCalls: filteredDebugLogs.length, totalInputTokens: filteredDebugLogs.reduce((s, l) => s + l.inputTokens, 0), totalOutputTokens: filteredDebugLogs.reduce((s, l) => s + l.outputTokens, 0) };

    // 发起调试：每个已选区域的模型生成一条记录
    const handleDebugSubmit = () => {
        if (!debugFile) { message.warning('请先上传模拟抓图'); return; }
        const activeRegions = (config.checkedRegions || []).filter(rk => {
            const m = config.regionModels?.[rk];
            return m?.model;
        });
        if (activeRegions.length === 0) { message.warning('没有已配置模型的区域，请先在「选择模型」中配置'); return; }
        setDebugLoading(true);
        setTimeout(() => {
            const newLogs = [];
            activeRegions.forEach(rk => {
                const label = REGIONS.find(r => r.key === rk)?.label || rk;
                const modelName = config.regionModels[rk].model;
                const inputTokens = Math.floor(Math.random() * 80) + 20;
                const outputTokens = Math.floor(Math.random() * 120) + 40;
                const latency = Math.floor(Math.random() * 1500) + 300;
                const isSuccess = Math.random() > 0.1;
                newLogs.push({
                    key: Date.now().toString() + '-' + rk,
                    time: new Date().toLocaleString('zh-CN', { hour12: false }),
                    model: `${modelName}（${label}）`,
                    image: debugFile.name,
                    inputContent: config.systemPrompt || '分析摄像头画面，识别异常事件...',
                    outputContent: isSuccess ? '{"event":"移动侦测","severity":"low","summary":"检测到轻微移动","detail":"画面左上角检测到持续性移动"}' : '',
                    inputTokens: isSuccess ? inputTokens : 0,
                    outputTokens: isSuccess ? outputTokens : 0,
                    status: isSuccess ? 'success' : 'failed',
                    latency: isSuccess ? latency : 3000,
                    traceId: 'trace-' + Math.random().toString(36).slice(2, 10),
                });
            });
            setFilteredLogs(prev => [...newLogs, ...prev]);
            if (newLogs.length === 1) {
                setDebugResult({ outputContent: newLogs[0].outputContent, inputTokens: newLogs[0].inputTokens, outputTokens: newLogs[0].outputTokens, latency: newLogs[0].latency, traceId: newLogs[0].traceId });
            }
            setDebugLoading(false);
            message.success(`调试完成，已生成 ${newLogs.length} 条记录`);
        }, 1500);
    };

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) { if (entry.isIntersecting) { setActiveSection(entry.target.id); break; } }
        }, { rootMargin: '-80px 0px -60% 0px' });
        SECTIONS.forEach(s => { const el = document.getElementById(s.key); if (el) observer.observe(el); });
        return () => observer.disconnect();
    }, []);

    if (loading) return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;
    if (!config) return <div style={{ padding: 64, textAlign: 'center' }}><Text type="secondary">Agent不存在</Text></div>;

    const statusTag = STATUS_MAP[config.status] || { text: config.status, color: 'default' };
    const showCaptureEvents = config.captureMode === 'event' || config.captureMode === 'mixed';
    const showCaptureTimer = config.captureMode === 'timer' || config.captureMode === 'mixed';

    const configContent = (
        <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ width: 140, flexShrink: 0 }}>
                <div style={{ position: 'sticky', top: 24 }}>
                    {SECTIONS.map(s => (
                        <div key={s.key} onClick={() => { setActiveSection(s.key); document.getElementById(s.key)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                            style={{ padding: '8px 12px', borderRadius: 6, cursor: 'pointer', marginBottom: 4, background: activeSection === s.key ? '#e6f4ff' : 'transparent', color: activeSection === s.key ? '#1677ff' : '#666', fontSize: 13 }}>
                            {s.title}
                        </div>
                    ))}
                </div>
            </div>
            <div style={{ flex: 1, maxWidth: 800 }}>
                {/* ① 基础信息 */}
                <div id="basic" style={{ marginBottom: 32 }}>
                    <Title level={5}>基础信息</Title>
                    <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <Form layout="vertical">
                            <Form.Item label="Agent名称" required><Input value={config.name} onChange={(e) => setConfig(prev => ({ ...prev, name: e.target.value }))} placeholder="请输入Agent名称" maxLength={30} /></Form.Item>
                            <Form.Item label="描述"><TextArea value={config.description} onChange={(e) => setConfig(prev => ({ ...prev, description: e.target.value }))} placeholder="Agent功能描述" maxLength={200} rows={2} /></Form.Item>
                        </Form>
                    </Card>
                </div>
                {/* ② 抓图策略 */}
                <div id="capture" style={{ marginBottom: 32 }}>
                    <Title level={5}>抓图策略 <Tag color="red" style={{ border: 'none', fontSize: 11 }}>必选</Tag></Title>
                    <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <Form layout="vertical">
                            <Form.Item label="抓图模式">
                                <Radio.Group value={config.captureMode} onChange={(e) => setConfig(prev => ({ ...prev, captureMode: e.target.value }))}>
                                    <Radio.Button value="event">⚡ 事件触发</Radio.Button><Radio.Button value="timer">⏱ 定时抓图</Radio.Button><Radio.Button value="mixed">🔀 混合模式</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                            {showCaptureEvents && <><Form.Item label="触发事件类型"><Checkbox.Group options={EVENT_OPTIONS} value={config.eventTypes} onChange={(v) => setConfig(prev => ({ ...prev, eventTypes: v }))} /></Form.Item>
                            <Form.Item label="触发事件间隔" required rules={[{ required: true, message: '请输入触发事件间隔' }]}> <InputNumber min={1} max={300} value={config.eventInterval} onChange={(v) => setConfig(prev => ({ ...prev, eventInterval: v }))} addonAfter="秒" /><div><Text type="secondary" style={{ fontSize: 12 }}>每次触发事件间隔多少秒进行事件图片识别</Text></div></Form.Item></>}
                            {showCaptureTimer && <Form.Item label="定时抓图间隔" required rules={[{ required: true, message: '请输入定时抓图间隔' }]}><InputNumber min={1} max={3600} value={config.timerInterval} onChange={(v) => setConfig(prev => ({ ...prev, timerInterval: v }))} addonAfter="秒" /></Form.Item>}
                            <Form.Item label="每日抓图上限默认值" required rules={[{ required: true, message: '请输入每日抓图上限' }]}><InputNumber min={1} max={288} value={config.dailyCaptureLimit} onChange={(v) => setConfig(prev => ({ ...prev, dailyCaptureLimit: v }))} addonAfter="张/天" /><div><Text type="secondary" style={{ fontSize: 12 }}>实际上限以套餐服务SKU规格为准，此值为建议默认值</Text></div></Form.Item>
                        </Form>
                    </Card>
                </div>
                {/* ③ 事件识别 */}
                <div id="recognition" style={{ marginBottom: 32 }}>
                    <Title level={5}>事件识别 <Switch checked={config.recognitionEnabled} onChange={(v) => setConfig(prev => ({ ...prev, recognitionEnabled: v }))} style={{ marginLeft: 8 }} /></Title>
                    <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        {!config.recognitionEnabled ? (
                            <Alert message="关闭事件识别后，抓图将不做AI分析处理" type="info" showIcon action={<Button size="small" onClick={() => setConfig(prev => ({ ...prev, recognitionEnabled: true }))}>开启事件识别</Button>} />
                        ) : (
                            <Form layout="vertical">
                                <Form.Item label={<><Text>temperature</Text><Tooltip title="控制输出随机性，建议低值（0.1~0.3）确保结构化输出准确性"><InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} /></Tooltip></>}>
                                    <Row gutter={16} align="middle"><Col flex="auto"><Slider min={0} max={1} step={0.05} value={config.temperature} onChange={(v) => setConfig(prev => ({ ...prev, temperature: v }))} /></Col><Col><Text strong>{config.temperature?.toFixed(2)}</Text></Col></Row>
                                </Form.Item>
                                <Form.Item label={<Space>系统提示词 <Text type="secondary" style={{ fontSize: 11 }}>{config.systemPrompt?.length || 0}/6000</Text></Space>}>
                                    <TextArea value={config.systemPrompt} onChange={(e) => setConfig(prev => ({ ...prev, systemPrompt: e.target.value }))} rows={4} maxLength={6000} placeholder="编写系统提示词..." />
                                    <Alert type="info" style={{ marginTop: 8, borderRadius: 6 }} message="支持变量：${device_name}设备名、${scene_type}场景类型、${event_type}事件类型、${timestamp}时间戳" /> {/* eslint-disable-line no-template-curly-in-string */}
                                </Form.Item>
                                <Form.Item label={<Text type="secondary" style={{ color: '#ff4d4f' }}>输出格式提示词（系统固定，不可编辑）</Text>}><TextArea value={config.outputFormatPrompt} readOnly style={{ background: '#f5f5f5' }} rows={3} /></Form.Item>
                            </Form>
                        )}
                    </Card>
                </div>
                {/* ④ AI搜索 */}
                <div id="search" style={{ marginBottom: 32 }}>
                    <Title level={5}>AI搜索 <Switch checked={config.searchEnabled} onChange={(v) => setConfig(prev => ({ ...prev, searchEnabled: v }))} style={{ marginLeft: 8 }} /></Title>
                    <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        {!config.searchEnabled ? (
                            <Alert message="关闭后无法查询历史识别结果" type="info" showIcon action={<Button size="small" onClick={() => setConfig(prev => ({ ...prev, searchEnabled: true }))}>开启AI搜索</Button>} />
                        ) : (
                            <Form layout="vertical">
                                <Form.Item label={<><Text>查询范围（数据保留天数）</Text><Tooltip title="检索近N天内的历史识别结果"><InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} /></Tooltip></>}>
                                    <Select value={config.searchRange} onChange={(v) => setConfig(prev => ({ ...prev, searchRange: v }))} options={[{ label: '近1天', value: 1 }, { label: '近3天', value: 3 }, { label: '近7天', value: 7 }, { label: '近14天', value: 14 }, { label: '近30天', value: 30 }]} />
                                </Form.Item>
                                <Form.Item label="返回条数 Top-K"><InputNumber min={1} max={50} value={config.searchTopK} onChange={(v) => setConfig(prev => ({ ...prev, searchTopK: v }))} /></Form.Item>
                                <Form.Item label={<><Text>相似度阈值</Text><Tooltip title="低于此阈值的结果被过滤"><InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} /></Tooltip></>}>
                                    <Row gutter={16} align="middle"><Col flex="auto"><Slider min={0} max={1} step={0.05} value={config.searchThreshold} onChange={(v) => setConfig(prev => ({ ...prev, searchThreshold: v }))} /></Col><Col><Text strong>{config.searchThreshold?.toFixed(2)}</Text></Col></Row>
                                </Form.Item>
                            </Form>
                        )}
                    </Card>
                </div>
                {/* ⑤ 阶段总结 */}
                <div id="summary" style={{ marginBottom: 32 }}>
                    <Title level={5}>阶段总结 <Switch checked={config.summaryEnabled} onChange={(v) => setConfig(prev => ({ ...prev, summaryEnabled: v }))} style={{ marginLeft: 8 }} /></Title>
                    <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        {!config.summaryEnabled ? (
                            <Alert message="关闭后用户不会收到周期性AI报告" type="info" showIcon action={<Button size="small" onClick={() => setConfig(prev => ({ ...prev, summaryEnabled: true }))}>开启阶段总结</Button>} />
                        ) : (
                            <Form layout="vertical">
                                <Form.Item label="总结范围">
                                    <Select value={config.summaryPeriod} style={{ width: 160 }}
                                        onChange={(v) => setConfig(prev => ({ ...prev, summaryPeriod: v }))}
                                        options={[
                                            { label: '每日', value: 'daily' },
                                            { label: '每周', value: 'weekly' },
                                            { label: '每月', value: 'monthly' },
                                            { label: '自定义范围', value: 'custom' },
                                        ]} />
                                </Form.Item>
                                {config.summaryPeriod === 'custom' && (
                                    <Form.Item label="总结范围天数">
                                        <InputNumber min={1} max={365} value={config.summaryRangeDays}
                                            onChange={(v) => setConfig(prev => ({ ...prev, summaryRangeDays: v }))}
                                            addonAfter="天" style={{ width: 160 }} />
                                    </Form.Item>
                                )}
                                <Form.Item label={`${config.summaryPeriod === 'daily' ? '每日' : config.summaryPeriod === 'weekly' ? '每周' : config.summaryPeriod === 'monthly' ? '每月' : '周期内'}最多总结次数`}>
                                    <InputNumber min={1} max={100} value={config.summaryLimit}
                                        onChange={(v) => setConfig(prev => ({ ...prev, summaryLimit: v }))}
                                        addonAfter="次" style={{ width: 160 }} />
                                </Form.Item>
                                <Form.Item label="总结提示词">
                                    <TextArea value={config.summaryPrompt} onChange={(e) => setConfig(prev => ({ ...prev, summaryPrompt: e.target.value }))} rows={4} placeholder="编写总结提示词..." />
                                    <Alert type="info" style={{ marginTop: 8, borderRadius: 6 }} message="变量：${period}周期描述、${events_list}事件列表、${previous_summary}上次总结" /> {/* eslint-disable-line no-template-curly-in-string */}
                                </Form.Item>
                                <Form.Item label={<Text type="secondary" style={{ color: '#ff4d4f' }}>输出格式提示词（系统固定，不可编辑）</Text>}><TextArea value={config.summaryOutputFormat} readOnly style={{ background: '#f5f5f5' }} rows={4} /></Form.Item>
                            </Form>
                        )}
                    </Card>
                </div>
                {/* ⑥ 选择模型（公共区域，按区域选） */}
                <div id="modelSelect" style={{ marginBottom: 32 }}>
                    <Title level={5}>选择模型 <Tag color="red" style={{ border: 'none', fontSize: 11 }}>必选</Tag></Title>
                    <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <Alert message="按服务区域选择模型，未勾选区域不生效" type="info" showIcon style={{ marginBottom: 16, borderRadius: 6 }} />
                        {REGIONS.map(r => {
                            const checked = (config.checkedRegions || []).includes(r.key);
                            const rm = config.regionModels?.[r.key] || {};
                            return (
                                <div key={r.key} style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: checked ? '#f6ffed' : '#fafafa', border: checked ? '1px solid #b7eb8f' : '1px solid #f0f0f0' }}>
                                    <Checkbox checked={checked} onChange={(e) => toggleRegion(r.key, e.target.checked)}>
                                        <Text strong style={{ fontSize: 13 }}>{r.label}</Text>
                                    </Checkbox>
                                    {checked && (
                                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <Select
                                                size="small"
                                                style={{ width: 220 }}
                                                value={rm.model}
                                                onChange={(v) => setRegionModel(r.key, 'model', v)}
                                                placeholder="选择模型"
                                                options={MODEL_OPTIONS.map(m => ({ label: m, value: m }))}
                                            />
                                            <Text type="secondary" style={{ fontSize: 11 }}>作用于：事件识别、阶段总结</Text>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </Card>
                </div>
                {/* ⑦ 通知配置 */}
                <div id="notify" style={{ marginBottom: 32 }}>
                    <Title level={5}>通知配置</Title>
                    <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <Form layout="vertical">
                            <Form.Item label="通知方式">
                                <Checkbox.Group
                                    options={NOTIFY_METHODS}
                                    value={config.notifyMethods}
                                    onChange={(v) => setConfig(prev => ({ ...prev, notifyMethods: v }))}
                                />
                                <div><Text type="secondary" style={{ fontSize: 12 }}>用于 Agent 产生事件告警时的通知渠道，可不选（不发送通知）</Text></div>
                            </Form.Item>
                        </Form>
                    </Card>
                </div>
            </div>
        </div>
    );

    const debugContent = (
        <div style={{ padding: '8px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <Space size={12} align="center">
                    <DatePicker.RangePicker showTime style={{ width: 360 }}
                        value={debugFilters.dateRange}
                        onChange={(v) => setDebugFilters(prev => ({ ...prev, dateRange: v }))} />
                    <Select allowClear placeholder="状态" style={{ width: 120 }}
                        value={debugFilters.status}
                        onChange={(v) => setDebugFilters(prev => ({ ...prev, status: v }))}
                        options={[{ label: '成功', value: 'success' }, { label: '失败', value: 'failed' }]} />
                    <Button type="primary" onClick={() => {}}>查询</Button>
                    <Button onClick={() => { setDebugFilters({ dateRange: null, status: undefined }); }}>重置</Button>
                </Space>
                <Button type="primary" icon={<BugOutlined />} onClick={() => { setDebugFile(null); setDebugResult(null); setDebugDrawer({ open: true }); }}>发起调试</Button>
            </div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}><Card size="small" bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 12 }}>调用次数</Text><div style={{ fontSize: 24, fontWeight: 600, color: '#1677ff', marginTop: 4 }}>{debugStats.totalCalls}</div></Card></Col>
                <Col span={8}><Card size="small" bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 12 }}>输入Token 总计</Text><div style={{ fontSize: 24, fontWeight: 600, color: '#52c41a', marginTop: 4 }}>{debugStats.totalInputTokens.toLocaleString()}</div></Card></Col>
                <Col span={8}><Card size="small" bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 12 }}>输出Token 总计</Text><div style={{ fontSize: 24, fontWeight: 600, color: '#fa8c16', marginTop: 4 }}>{debugStats.totalOutputTokens.toLocaleString()}</div></Card></Col>
            </Row>
            <Table dataSource={filteredDebugLogs} size="small" pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }} scroll={{ x: 1200 }}
                columns={[
                    { title: '时间', dataIndex: 'time', width: 170 },
                    { title: '模型', dataIndex: 'model', width: 170, render: (v) => <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{v}</Text> },
                    { title: '图片', dataIndex: 'image', width: 160, render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> },
                    { title: '输入内容', dataIndex: 'inputContent', width: 160, ellipsis: true },
                    { title: '输出内容', dataIndex: 'outputContent', width: 160, ellipsis: true, render: (v) => v || <Text type="secondary">--</Text> },
                    { title: '输入Token', dataIndex: 'inputTokens', width: 80, align: 'center' },
                    { title: '输出Token', dataIndex: 'outputTokens', width: 80, align: 'center' },
                    { title: '状态', dataIndex: 'status', width: 70, align: 'center', render: (v) => <Tag color={v === 'success' ? 'green' : 'red'} style={{ border: 'none' }}>{v === 'success' ? '成功' : '失败'}</Tag> },
                    { title: '延迟', dataIndex: 'latency', width: 80, align: 'center', render: (v) => `${v}ms` },
                    { title: 'Trace ID', dataIndex: 'traceId', width: 140, render: (v) => <Text copyable style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> },
            ]} />

            <Drawer title="发起调试" open={debugDrawer.open} onClose={() => { setDebugDrawer({ open: false }); setDebugResult(null); }} width={640}>
                <Form layout="vertical">
                    <Form.Item label="模拟抓图" required>
                        <Upload maxCount={1} beforeUpload={() => false}
                            fileList={debugFile ? [debugFile] : []}
                            onChange={({ fileList }) => { const f = fileList[fileList.length - 1]; setDebugFile(f && f.originFileObj ? f.originFileObj : null); }}
                            onRemove={() => setDebugFile(null)}>
                            <Button icon={<UploadOutlined />}>选择图片</Button>
                        </Upload>
                        {debugFile && <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>已选择：{debugFile.name}（{(debugFile.size / 1024).toFixed(1)} KB）</Text>}
                    </Form.Item>
                    <Form.Item label="生效区域">
                        {(config.checkedRegions || []).length > 0 ? (
                            <div>
                                {(config.checkedRegions || []).map(rk => {
                                    const label = REGIONS.find(r => r.key === rk)?.label || rk;
                                    const rm = config.regionModels?.[rk] || {};
                                    return (
                                        <div key={rk} style={{ marginBottom: 4 }}>
                                            <Tag color="blue" style={{ border: 'none', marginRight: 4 }}>{label}</Tag>
                                            <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{rm.model || '--'}</Text>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : <Text type="secondary">未配置任何区域模型</Text>}
                    </Form.Item>
                    <Form.Item label="系统提示词">
                        <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 6, maxHeight: 160, overflow: 'auto' }}>
                            <Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{config.systemPrompt || '（未配置系统提示词）'}</Text>
                        </div>
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" icon={<BugOutlined />} loading={debugLoading} onClick={handleDebugSubmit} block>
                            开始调试
                        </Button>
                    </Form.Item>
                </Form>
                {debugResult && (
                    <>
                        <Divider />
                        <div style={{ padding: 12, background: '#f6ffed', borderRadius: 8, marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>最新输出结果</Text>
                            <pre style={{ margin: '8px 0 0', fontSize: 12, whiteSpace: 'pre-wrap' }}>{debugResult.outputContent}</pre>
                        </div>
                        <Row gutter={16} style={{ marginBottom: 8 }}>
                            <Col span={6}><Text type="secondary" style={{ fontSize: 11 }}>输入Token</Text><div><Text strong>{debugResult.inputTokens}</Text></div></Col>
                            <Col span={6}><Text type="secondary" style={{ fontSize: 11 }}>输出Token</Text><div><Text strong>{debugResult.outputTokens}</Text></div></Col>
                            <Col span={6}><Text type="secondary" style={{ fontSize: 11 }}>延迟</Text><div><Text strong>{debugResult.latency}ms</Text></div></Col>
                            <Col span={6}><Text type="secondary" style={{ fontSize: 11 }}>Trace ID</Text><div><Text style={{ fontFamily: 'monospace', fontSize: 12 }} copyable>{debugResult.traceId}</Text></div></Col>
                        </Row>
                        <Text type="secondary" style={{ fontSize: 11 }}>内容由AI生成，仅供参考</Text>
                    </>
                )}
            </Drawer>
        </div>
    );

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/agent/list')}>返回列表</Button>
                    <Divider type="vertical" />
                    <Input style={{ width: 260 }} value={config.name} onChange={(e) => setConfig(prev => ({ ...prev, name: e.target.value }))} placeholder="Agent名称" bordered={false} size="large" />
                    <Tag color={statusTag.color} style={{ border: 'none', fontSize: 13, padding: '2px 10px' }}>{statusTag.text}</Tag>
                    {!isNew && <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>{config.agentId}</Text>}
                </Space>
                <Space>
                    <Button icon={<SaveOutlined />} onClick={handleSave}>保存</Button>
                    {config.status === 'draft' && <Button type="primary" icon={<PlayCircleOutlined />} onClick={handlePublish}>发布</Button>}
                    {config.status === 'published' && <Button danger icon={<StopOutlined />} onClick={handleOffline}>下线</Button>}
                    {config.status === 'offline' && <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => setConfig(prev => ({ ...prev, status: 'published' }))}>重新发布</Button>}
                </Space>
            </div>
            <Tabs activeKey={pageTab} onChange={setPageTab} items={[
                { key: 'config', label: 'Agent配置', children: configContent },
                { key: 'debug', label: 'Agent调试', children: debugContent },
            ]} />
        </div>
    );
};

export default AgentConfig;
